import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { Department, Route, Landmark } from "../types";
import "mapbox-gl/dist/mapbox-gl.css";
import FloorTransitionOverlay from "./FloorTransitionOverlay";
import { createKioskMarker } from "./kioskMarker";

interface InteractiveMapProps {
  departments: Department[];
  landmarks?: Landmark[];
  selectedDepartment?: Department;
  selectedDestination?: Department | Landmark | null;
  onDepartmentSelect?: (dept: Department) => void;
  height?: number | string;
  kioskId?: string;
  onRouteFound?: (route: any) => void;
  kioskData?: any;
  route?: Route | null;
  currentStepIndex?: number;
  highlightKiosk?: boolean;
  showAllLocations?: boolean;
  onFloorChange?: (floor: string) => void;
}

const MAPBOX_API_KEY =
  "pk.eyJ1IjoiaW5hYW11cnJlaG1hbiIsImEiOiJjbWxxbWtldWUwMmRyM2ZzYmF3Z2NyeXNtIn0.QdmkobugXfeqHrHZXntbtQ";
mapboxgl.accessToken = MAPBOX_API_KEY;

const InteractiveMap: React.FC<InteractiveMapProps> = ({
  departments,
  landmarks = [],
  selectedDepartment,
  selectedDestination,
  onDepartmentSelect,
  height = 600,
  kioskId = "K1",
  onRouteFound,
  kioskData,
  route,
  currentStepIndex = 0,
  highlightKiosk = false,
  showAllLocations = false,
  onFloorChange,
}) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<Map<string, mapboxgl.Marker>>(new Map());
  const kioskMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const mapLoadedRef = useRef<boolean>(false);
  const [routeLineId] = useState("route-line");
  const [stepMarkerRef, setStepMarkerRef] = useState<mapboxgl.Marker | null>(
    null,
  );
  const [isSimulating, setIsSimulating] = useState(false);
  const [routeCoordinates, setRouteCoordinates] = useState<[number, number][]>(
    [],
  );
  const simulationRef = useRef<number | null>(null);
  const pendingHighlightRef = useRef<string>("");
  const pulseRafRef = useRef<number | null>(null);
  const isAutoFloorSwitchRef = useRef<boolean>(false);
  const isNavigatingRef = useRef<boolean>(false);
  const savedKioskCoordsRef = useRef<[number, number] | null>(null);
  const savedDestCoordsRef = useRef<[number, number] | null>(null);
  const savedKioskFloorRef = useRef<string>("G");
  const destPinMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const highlightIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const show3DZones = true; // always on — Hide 3D button removed
  const [zonesData, setZonesData] = useState<any[]>([]);
  const [activeFloor, setActiveFloor] = useState<"G" | "B1">("G");
  const [isFloorTransitioning, setIsFloorTransitioning] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState<"G" | "B1">("B1");
  const [transitionFromFloor, setTransitionFromFloor] = useState<"G" | "B1">(
    "G",
  );

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      Emergency: "#d32f2f",
      OPD: "#1976d2",
      Labs: "#388e3c",
      Pharmacy: "#f57c00",
      Radiology: "#7b1fa2",
      Surgery: "#c2185b",
      ICU: "#d32f2f",
      Pediatrics: "#00897b",
      Cardiology: "#e91e63",
      Billing: "#fbc02d",
      Other: "#757575",
    };
    return colors[category] || "#1976d2";
  };

  // Load zones data for 3D rendering
  useEffect(() => {
    fetch("/zones-data.json")
      .then((res) => res.json())
      .then((data) => {
        if (data.data && Array.isArray(data.data)) {
          setZonesData(data.data);
          console.log(`✅ Loaded ${data.data.length} zones for 3D rendering`);
        }
      })
      .catch((err) => console.error("Failed to load zones:", err));
  }, []);

  useEffect(() => {
    if (!mapContainer.current) return;

    if (!mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/empty-v9",
        center: [46.623, 24.81145],
        zoom: 19.7,
        minZoom: 6,
        pitch: 50,
        bearing: 65,
        maxBounds: [
          [46.6217, 24.8102],
          [46.6257, 24.8133],
        ],
      });

      mapRef.current.addControl(new mapboxgl.NavigationControl());

      // Set solid background color on style load
      mapRef.current.on("style.load", () => {
        const map = mapRef.current!;
        // Add a solid background layer
        if (!map.getLayer("background")) {
          map.addLayer(
            {
              id: "background",
              type: "background",
              paint: { "background-color": "#0F172A" }, // dark theme background
            },
            undefined,
          );
        }
      });

      // add 3D buildings layer when style is ready
      mapRef.current.on("load", () => {
        mapLoadedRef.current = true;
        const map = mapRef.current!;

        // find the first symbol layer to place custom models below labels
        const layers = map.getStyle().layers || [];
        let labelLayerId: string | undefined;
        for (const l of layers) {
          if (l.type === "symbol" && (l.layout as any)?.["text-field"]) {
            labelLayerId = l.id;
            break;
          }
        }

        // Note: 3D buildings and GLB models removed for cleaner map view

        // Add GeoJSON source — use kiosk's floor to pick the right file
        const kioskFloorInit = (kioskData as any)?.floor || "G";
        const initialGeoJSON =
          kioskFloorInit === "B1"
            ? "/B1_floor_map_nav.geojson"
            : "/ground_floor_combined.geojson";

        // Set initial active floor to match kiosk
        if (kioskFloorInit !== "G") {
          setActiveFloor(kioskFloorInit as "G" | "B1");
        }

        map.addSource("ground-floor", {
          type: "geojson",
          data: initialGeoJSON,
        });

        // svg_zone fills first (background zones)
        map.addLayer({
          id: "zones-fill",
          type: "fill",
          source: "ground-floor",
          filter: ["==", ["get", "layer"], "svg_zone"],
          paint: {
            "fill-color": ["coalesce", ["get", "fill"], "rgba(0,0,0,0)"],
            "fill-opacity": 1,
          },
        });

        map.addLayer({
          id: "zones-outline",
          type: "line",
          source: "ground-floor",
          filter: ["==", ["get", "layer"], "svg_zone"],
          paint: {
            "line-color": ["coalesce", ["get", "stroke"], "#b0b0b0"],
            "line-width": 0.5,
            "line-opacity": 0.75,
          },
        });

        // 2px black border on the base boundary polygon (fill #FAF9F6)
        map.addLayer({
          id: "boundary-base-outline",
          type: "line",
          source: "ground-floor",
          filter: [
            "all",
            ["==", ["geometry-type"], "Polygon"],
            ["==", ["get", "layer"], "svg_zone"],
            ["==", ["get", "fill"], "#FAF9F6"],
          ],
          paint: {
            "line-color": "#334155",
            "line-width": 3,
            "line-opacity": 1,
          },
        });

        // poi_zone fills on top (lifts, toilets, etc.)
        map.addLayer({
          id: "poi-fill",
          type: "fill",
          source: "ground-floor",
          filter: [
            "all",
            ["==", ["geometry-type"], "Polygon"],
            ["==", ["get", "layer"], "poi_zone"],
          ],
          paint: {
            "fill-color": ["coalesce", ["get", "fill"], "rgba(0,0,0,0)"],
            "fill-opacity": 1,
          },
        });

        map.addLayer({
          id: "poi-outline",
          type: "line",
          source: "ground-floor",
          filter: [
            "all",
            ["==", ["geometry-type"], "Polygon"],
            ["==", ["get", "layer"], "poi_zone"],
          ],
          paint: {
            "line-color": "#2d3561", //"#b0b0b0",
            "line-width": 0.5,
            "line-opacity": 0.75,
          },
        });

        // Labels for all poi_point features
        // 2.5D extrusion layer — hidden by default, toggled by Show 3D Zones button
        map.addLayer({
          id: "zones-3d-extrusion",
          type: "fill-extrusion",
          source: "ground-floor",
          filter: [
            "all",
            ["==", ["geometry-type"], "Polygon"],
            ["has", "height"],
          ],
          layout: { visibility: "visible" },
          paint: {
            "fill-extrusion-color": ["coalesce", ["get", "fill"], "#aaaaaa"],
            "fill-extrusion-height": ["coalesce", ["get", "height"], 3],
            "fill-extrusion-base": 0,
            "fill-extrusion-opacity": 1,
          },
        });

        // Destination highlight layer — polygon fill for selected destination
        map.addLayer({
          id: "destination-highlight",
          type: "fill",
          source: "ground-floor",
          filter: ["==", ["get", "name"], ""],
          paint: {
            "fill-color": "#d60202",
            "fill-opacity": 1,
          },
        });

        // Destination highlight — 3D extrusion so the full height is highlighted
        map.addLayer({
          id: "destination-highlight-extrusion",
          type: "fill-extrusion",
          source: "ground-floor",
          filter: ["==", ["get", "name"], ""],
          paint: {
            "fill-extrusion-color": "#d60202",
            "fill-extrusion-height": ["coalesce", ["get", "height"], 3],
            "fill-extrusion-base": 0,
            "fill-extrusion-opacity": 1,
          },
        });

        // Destination highlight outline
        /*map.addLayer({
          id: "destination-highlight-outline",
          type: "line",
          source: "ground-floor",
          filter: ["==", ["get", "name"], ""],
          paint: {
            "line-color": "#000000",
            "line-width": 0.25,
            "line-opacity": 0.25,
          },
        });
        */

        // Apply any pending highlight (destination selected before map loaded)
        if (pendingHighlightRef.current) {
          const name = pendingHighlightRef.current;
          const f: any = [
            "all",
            ["==", ["geometry-type"], "Polygon"],
            [
              "any",
              ["==", ["get", "layer"], "poi_zone"],
              ["==", ["get", "layer"], "svg_zone"],
            ],
            ["==", ["downcase", ["get", "name"]], name.toLowerCase()],
          ];
          map.setFilter("destination-highlight", f);
          map.setFilter("destination-highlight-extrusion", f);
          map.setFilter("destination-highlight-outline", f);
        }

        // poi_point labels — elevated above polygons for 3D readability
        map.addLayer({
          id: "poi-labels",
          type: "symbol",
          source: "ground-floor",
          filter: [
            "all",
            ["==", ["geometry-type"], "Point"],
            ["==", ["get", "layer"], "poi_point"],
            ["has", "name"],
          ],
          layout: {
            "text-field": ["get", "name"],
            "text-size": 13,
            "text-anchor": "center",
            "text-offset": [0, 0],
            "text-allow-overlap": false,
            "text-max-width": 10,
            "symbol-z-order": "viewport-y",
            "text-pitch-alignment": "viewport",
            "text-rotation-alignment": "viewport",
          },
          paint: {
            "text-color": "#111111",
            "text-halo-color": "rgba(255,255,255,0.95)",
            "text-halo-width": 1.25,
            "text-translate": [0, -20],
            "text-translate-anchor": "viewport",
          },
        });

        // Load SVG icons and add poi_point icon layer
        const iconDefs = [
          { name: "icon-lift", src: "/Icons/Elevator.svg" },
          { name: "icon-restroom", src: "/Icons/Restroom.svg" },
          { name: "icon-escalator", src: "/Icons/Escalator.svg" },
          { name: "icon-coffee", src: "/Icons/Coffee.svg" },
          { name: "icon-cafeteria", src: "/Icons/Cafeteria.svg" },
          { name: "icon-atm", src: "/Icons/AtmSign.svg" },
        ];

        const loadImage = (id: string, src: string) =>
          new Promise<void>((resolve) => {
            map.loadImage(src, (err, img) => {
              if (!err && img && !map.hasImage(id)) map.addImage(id, img);
              resolve();
            });
          });

        Promise.all(iconDefs.map((d) => loadImage(d.name, d.src))).then(() => {
          // Map category/name → icon id (all same size via icon-size)
          map.addLayer({
            id: "poi-point-icons",
            type: "symbol",
            source: "ground-floor",
            filter: [
              "all",
              ["==", ["geometry-type"], "Point"],
              ["==", ["get", "layer"], "poi_point"],
            ],
            layout: {
              "icon-image": [
                "case",
                ["in", "LIFT", ["upcase", ["get", "name"]]],
                "icon-lift",
                ["in", "TOILET", ["upcase", ["get", "name"]]],
                "icon-restroom",
                ["in", "RESTROOM", ["upcase", ["get", "name"]]],
                "icon-restroom",
                ["in", "ESCALAT", ["upcase", ["get", "name"]]],
                "icon-escalator",
                ["in", "COFFEE", ["upcase", ["get", "name"]]],
                "icon-coffee",
                ["in", "COSTA", ["upcase", ["get", "name"]]],
                "icon-coffee",
                ["in", "SANDWICH", ["upcase", ["get", "name"]]],
                "icon-cafeteria",
                ["in", "ATM", ["upcase", ["get", "name"]]],
                "icon-atm",
                "", // fallback — no icon
              ],
              "icon-size": 0.06, // uniform size for all icons
              "icon-allow-overlap": true,
              "icon-anchor": "center",
              "symbol-z-order": "viewport-y",
            },
          });
        });

        // Add route line source and layer for route visualization
        map.addSource("route-line-source", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });

        map.addLayer({
          id: routeLineId,
          type: "line",
          source: "route-line-source",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "#221cc7",
            "line-width": 12,
            "line-opacity": 1,
          },
        });

        // White dashed overlay for direction flow
        /*
        map.addLayer({
          id: "route-arrows",
          type: "line",
          source: "route-line-source",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "#FFFFFF",
            "line-width": 1,
            "line-opacity": 0.8,
            "line-dasharray": [0, 2, 2],
          },
        });
        */

        // Kiosk "You are here" marker
        if (highlightKiosk) {
          let kioskLat = 24.812;
          let kioskLon = 46.624;

          if (kioskData?.coordinates) {
            if (
              kioskData.coordinates.latitude &&
              kioskData.coordinates.longitude
            ) {
              kioskLat = kioskData.coordinates.latitude;
              kioskLon = kioskData.coordinates.longitude;
            } else {
              const pixelsPerMeter = 10;
              const earthRadius = 6371000;
              const baseLat = 24.812;
              const baseLon = 46.624;
              const yMeters = kioskData.coordinates.y / pixelsPerMeter;
              const xMeters = kioskData.coordinates.x / pixelsPerMeter;
              kioskLat = baseLat + (yMeters / earthRadius) * (180 / Math.PI);
              kioskLon =
                baseLon +
                (xMeters /
                  (earthRadius * Math.cos((baseLat * Math.PI) / 180))) *
                  (180 / Math.PI);
            }
          }

          const kioskMarker = createKioskMarker(map, [kioskLon, kioskLat], {
            bilingual: true,
          });
          kioskMarkersRef.current.push(kioskMarker);

          // Hide if kiosk is not on the currently active floor
          // Use kioskFloorInit (not activeFloor) — React state is async and still "G" here
          const kFloor = (kioskData as any)?.floor || "G";
          if (kFloor !== kioskFloorInit) {
            kioskMarker.getElement().style.display = "none";
          }
        }
      });
    }

    // POI dot markers removed — labels rendered via poi-labels map layer instead

    return () => {
      // Only cleanup on unmount, not on dependency changes
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departments]);

  // Toggle 3D zones layer
  useEffect(() => {
    if (!mapRef.current || !mapLoadedRef.current) return;

    const map = mapRef.current;

    if (show3DZones) {
      // Show the 3D extrusion layer
      if (map.getLayer("zones-3d-extrusion")) {
        map.setLayoutProperty("zones-3d-extrusion", "visibility", "visible");

        map.setPaintProperty(
          "zones-3d-extrusion",
          "fill-extrusion-opacity",
          1, // try 0.3 – 0.6
        );
        console.log("✅ 3D zones layer shown");
      }
    } else {
      // Hide the 3D extrusion layer
      if (map.getLayer("zones-3d-extrusion")) {
        map.setLayoutProperty("zones-3d-extrusion", "visibility", "none");
        console.log("✅ 3D zones layer hidden");
      }
    }
  }, [show3DZones]);

  // Landmark dot markers removed — labels rendered via poi-labels map layer
  useEffect(() => {
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landmarks]);

  // Building center — used to calculate dynamic bearing from any kiosk
  const BUILDING_CENTER: [number, number] = [46.62315, 24.8115]; //[46.6237, 24.8117];

  const calcBearingToBuilding = (
    kioskLon: number,
    kioskLat: number,
  ): number => {
    const dLon = ((BUILDING_CENTER[0] - kioskLon) * Math.PI) / 180;
    const lat1 = (kioskLat * Math.PI) / 180;
    const lat2 = (BUILDING_CENTER[1] * Math.PI) / 180;
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  };

  // Offset center forward along bearing so kiosk appears at bottom of screen
  const offsetCenter = (
    lon: number,
    lat: number,
    bearing: number,
  ): [number, number] => {
    const dist = 0.0004; // ~44m offset
    const bearingRad = (bearing * Math.PI) / 180;
    return [
      lon + dist * Math.sin(bearingRad),
      lat + dist * Math.cos(bearingRad),
    ];
  };

  // Center map on kiosk dynamically when kiosk data loads
  useEffect(() => {
    if (!kioskData?.coordinates || !mapRef.current || !mapLoadedRef.current)
      return;
    const lat = kioskData.coordinates.latitude;
    const lon = kioskData.coordinates.longitude;
    if (!lat || !lon) return;

    // Switch to the kiosk's floor automatically
    const kioskFloor = (kioskData as any).floor || "G";
    if (kioskFloor !== activeFloor) {
      switchFloor(kioskFloor as "G" | "B1");
    }

    const bearing = calcBearingToBuilding(lon, lat);
    const [cLon, cLat] = offsetCenter(lon, lat, bearing);

    mapRef.current.easeTo({
      center: [cLon, cLat],
      zoom: 19,
      pitch: 55,
      bearing: 66,
      duration: 1000,
    });
  }, [kioskData]);

  // Toggle 3D extrusion layer
  useEffect(() => {
    if (!mapRef.current || !mapLoadedRef.current) return;
    const map = mapRef.current;
    if (map.getLayer("zones-3d-extrusion")) {
      map.setLayoutProperty(
        "zones-3d-extrusion",
        "visibility",
        show3DZones ? "visible" : "none",
      );
      if (show3DZones) map.easeTo({ pitch: 60, duration: 600 });
      else map.easeTo({ pitch: 45, duration: 600 });
    }
  }, [show3DZones]);

  // Cleanup all markers and map only on unmount
  useEffect(() => {
    // Listen for window resize to make Mapbox fill its container
    const handleResize = () => {
      if (mapRef.current) {
        mapRef.current.resize();
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (highlightIntervalRef.current !== null) {
        clearInterval(highlightIntervalRef.current);
        highlightIntervalRef.current = null;
      }
      if (pulseRafRef.current !== null) {
        cancelAnimationFrame(pulseRafRef.current);
        pulseRafRef.current = null;
      }
      if (destPinMarkerRef.current) {
        destPinMarkerRef.current.remove();
        destPinMarkerRef.current = null;
      }
      markersRef.current.forEach((m) => m.remove());
      markersRef.current.clear();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      mapLoadedRef.current = false;
    };
  }, []);

  // Inject destination pin keyframes once
  useEffect(() => {
    const styleId = "dest-pin-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes destPinDrop {
          0%   { transform: translateY(-40px) scale(0.7); opacity: 0; }
          60%  { transform: translateY(6px)   scale(1.1); opacity: 1; }
          80%  { transform: translateY(-4px)  scale(0.95); }
          100% { transform: translateY(0)     scale(1);   opacity: 1; }
        }
        @keyframes destPinBounce {
          0%, 100% { transform: translateY(0)   scale(1); }
          50%       { transform: translateY(-10px) scale(1.08); }
        }
        @keyframes destPinShadow {
          0%, 100% { transform: scale(1);   opacity: 0.35; }
          50%       { transform: scale(0.6); opacity: 0.15; }
        }
        .dest-pin-img {
          animation: destPinDrop 0.55s cubic-bezier(.36,.07,.19,.97) forwards,
                     destPinBounce 1.6s ease-in-out 0.55s infinite;
        }
        .dest-pin-shadow {
          width: 20px; height: 6px;
          background: rgba(0,0,0,0.25);
          border-radius: 50%;
          margin: 0 auto;
          animation: destPinShadow 1.6s ease-in-out 0.55s infinite;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Highlight selected destination (department or landmark)
  useEffect(() => {
    let destination = selectedDestination || selectedDepartment;
    const destName = destination?.name || "";

    // Store current destination for switchFloor to reference
    (window as any).__currentDestination = destination || null;

    // Cancel any stale polling interval from a previous effect run
    if (highlightIntervalRef.current !== null) {
      clearInterval(highlightIntervalRef.current);
      highlightIntervalRef.current = null;
    }

    // Cancel any old RAF pulse (legacy — kept for safety)
    if (pulseRafRef.current !== null) {
      cancelAnimationFrame(pulseRafRef.current);
      pulseRafRef.current = null;
    }

    // Remove previous destination pin
    if (destPinMarkerRef.current) {
      destPinMarkerRef.current.remove();
      destPinMarkerRef.current = null;
    }

    const applyHighlight = () => {
      const map = mapRef.current;
      if (!map || !mapLoadedRef.current) return false;
      if (!map.getLayer("destination-highlight")) return false;

      // Safety: remove any pin that may have been placed by a stale interval
      if (destPinMarkerRef.current) {
        destPinMarkerRef.current.remove();
        destPinMarkerRef.current = null;
      }

      const highlightFilter: any = destName
        ? [
            "all",
            ["==", ["geometry-type"], "Polygon"],
            [
              "any",
              ["==", ["get", "layer"], "poi_zone"],
              ["==", ["get", "layer"], "svg_zone"],
            ],
            ["==", ["downcase", ["get", "name"]], destName.toLowerCase()],
          ]
        : ["==", ["get", "name"], ""];

      map.setFilter("destination-highlight", highlightFilter);
      map.setFilter("destination-highlight-extrusion", highlightFilter);
      map.setFilter("destination-highlight-outline", highlightFilter);
      // Keep fill static — no RAF pulse
      map.setPaintProperty("destination-highlight", "fill-opacity", 0.45);
      map.setPaintProperty("destination-highlight-outline", "line-width", 2.5);
      map.setPaintProperty("destination-highlight-outline", "line-opacity", 1);

      // Place animated pin only if destination is on the currently active floor
      if (destination) {
        const destFloor = (destination as any)?.floor || "G";
        const lat = destination.coordinates.latitude;
        const lon = destination.coordinates.longitude;
        if (lat && lon && destFloor === activeFloor) {
          const wrapper = document.createElement("div");
          wrapper.style.cssText =
            "display:flex;flex-direction:column;align-items:center;pointer-events:none;";

          const img = document.createElement("img");
          img.src = "/Icons/locationpin.png";
          img.className = "dest-pin-img";
          img.style.cssText =
            "width:40px;height:40px;object-fit:contain;filter:drop-shadow(0 4px 6px rgba(0,0,0,0.4));";

          const shadow = document.createElement("div");
          shadow.className = "dest-pin-shadow";

          wrapper.appendChild(img);
          wrapper.appendChild(shadow);

          destPinMarkerRef.current = new mapboxgl.Marker({
            element: wrapper,
            anchor: "bottom",
            offset: [0, 6],
          })
            .setLngLat([lon, lat])
            .addTo(map);
        }
      }

      return true;
    };

    // Store for re-application after map load
    pendingHighlightRef.current = destName;

    if (!applyHighlight()) {
      highlightIntervalRef.current = setInterval(() => {
        if (applyHighlight()) {
          clearInterval(highlightIntervalRef.current!);
          highlightIntervalRef.current = null;
        }
      }, 100);
      setTimeout(() => {
        if (highlightIntervalRef.current !== null) {
          clearInterval(highlightIntervalRef.current);
          highlightIntervalRef.current = null;
        }
      }, 5000);
    }

    if (!destination) {
      markersRef.current.forEach((marker) => {
        const el = marker.getElement() as HTMLElement;
        if (!el) return;
        el.style.width = "8px";
        el.style.height = "8px";
        el.style.boxShadow = "0 1px 2px rgba(0,0,0,0.3)";
        el.style.transform = "none";
      });
      return;
    }

    // Highlight the selected destination marker dot
    markersRef.current.forEach((marker, id) => {
      const el = marker.getElement() as HTMLElement;
      if (!el) return;
      if (destination && id === destination.id) {
        el.style.width = "12px";
        el.style.height = "12px";
        el.style.boxShadow = "0 2px 4px rgba(0,0,0,0.5)";
        el.style.transform = "translateY(-3px)";
      } else {
        el.style.width = "8px";
        el.style.height = "8px";
        el.style.boxShadow = "0 1px 2px rgba(0,0,0,0.3)";
        el.style.transform = "none";
      }
    });
  }, [selectedDestination, selectedDepartment, activeFloor]);

  // Fetch indoor route using GeoJSON navigation graph
  const fetchIndoorRoute = async (
    start: [number, number],
    end: [number, number],
    floorOverride?: string,
  ) => {
    try {
      console.log("📍 fetchIndoorRoute called with:", { start, end });

      // Load the GeoJSON file — use floorOverride if provided, else activeFloor
      const floorToUse = floorOverride || activeFloor;
      const geojsonFile =
        floorToUse === "B1"
          ? "/B1_floor_map_nav.geojson"
          : "/ground_floor_combined.geojson";
      const response = await fetch(geojsonFile);
      const geojson = await response.json();

      // Use routing_node Point features — these are the dense corridor dots
      // The nav_graph MultiPoint only has sparse gate nodes (50-160m apart)
      // routing_node features cover all corridors with ~4-8m spacing
      let nodes: Array<{ id: number; longitude: number; latitude: number }>;

      const navGraphFeature = geojson.features.find(
        (f: any) => f.properties?.layer === "nav_graph",
      );

      if (navGraphFeature && navGraphFeature.geometry.type === "MultiPoint") {
        nodes = navGraphFeature.geometry.coordinates.map(
          (coord: number[], index: number) => ({
            id: index,
            longitude: coord[0],
            latitude: coord[1],
          }),
        );
        console.log(`📊 Using nav_graph: ${nodes.length} nodes`);
      } else {
        // Fallback: extract from Point features with layer === "routing_node"
        nodes = geojson.features
          .filter(
            (f: any) =>
              f.geometry?.type === "Point" &&
              f.properties?.layer === "routing_node",
          )
          .map((f: any, index: number) => ({
            id: index,
            longitude: f.geometry.coordinates[0],
            latitude: f.geometry.coordinates[1],
          }));
        console.log(`📊 Fallback routing_node points: ${nodes.length} nodes`);
      }

      if (nodes.length === 0) {
        console.warn("❌ No navigation nodes found in GeoJSON");
        return null;
      }

      // Build graph - connect nearby nodes using K-nearest neighbors
      // This ensures turns are not skipped by connecting only truly adjacent nodes

      // Haversine distance in meters — shared by buildGraph and ensureConnected
      const haversineMeters = (
        lon1: number,
        lat1: number,
        lon2: number,
        lat2: number,
      ): number => {
        const R = 6371000;
        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLon = ((lon2 - lon1) * Math.PI) / 180;
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      };

      const buildGraph = () => {
        const graph: Map<number, { node: any; neighbors: number[] }> =
          new Map();

        // Max edge length: 8m — balanced to follow corridors without diagonal shortcuts
        const MAX_EDGE_METERS = 8;

        nodes.forEach((node: any) => {
          const candidates: { id: number; dist: number }[] = [];
          nodes.forEach((other: any) => {
            if (other.id === node.id) return;
            const d = haversineMeters(
              node.longitude,
              node.latitude,
              other.longitude,
              other.latitude,
            );
            if (d <= MAX_EDGE_METERS) {
              candidates.push({ id: other.id, dist: d });
            }
          });

          candidates.sort((a, b) => a.dist - b.dist);
          // Connect to 4 nearest — enough for turns without diagonal shortcuts
          const neighbors = candidates.slice(0, 4).map((c) => c.id);
          graph.set(node.id, { node, neighbors });
        });

        console.log(`🔗 Graph statistics:`);
        let totalConnections = 0;
        let nodesWithNoConnections = 0;
        graph.forEach((value) => {
          totalConnections += value.neighbors.length;
          if (value.neighbors.length === 0) nodesWithNoConnections++;
        });
        console.log(
          `   - Average connections per node: ${(totalConnections / graph.size).toFixed(2)}`,
        );
        console.log(
          `   - Nodes with no connections: ${nodesWithNoConnections}`,
        );

        return graph;
      };

      const graph = buildGraph();
      console.log(`🔗 Built graph with ${graph.size} navigation nodes`);

      // Ensure a node is connected to the graph — used for isolated start/end nodes
      const ensureConnected = (nodeId: number) => {
        const entry = graph.get(nodeId);
        if (!entry || entry.neighbors.length > 0) return;
        const allDists = nodes
          .filter((n: any) => n.id !== nodeId)
          .map((n: any) => ({
            id: n.id,
            dist: haversineMeters(
              entry.node.longitude,
              entry.node.latitude,
              n.longitude,
              n.latitude,
            ),
          }))
          .sort((a: any, b: any) => a.dist - b.dist);
        entry.neighbors = allDists.slice(0, 2).map((n: any) => n.id);
        allDists.slice(0, 2).forEach((n: any) => {
          const neighbor = graph.get(n.id);
          if (neighbor && !neighbor.neighbors.includes(nodeId)) {
            neighbor.neighbors.push(nodeId);
          }
        });
      };

      // Find nearest navigation node to start and end
      const findNearestNode = (coord: [number, number]) => {
        let nearest: any = null;
        let minDist = Infinity;

        nodes.forEach((node: any) => {
          const lon = node.longitude;
          const lat = node.latitude;
          const dist = Math.sqrt(
            Math.pow(lon - coord[0], 2) + Math.pow(lat - coord[1], 2),
          );
          if (dist < minDist) {
            minDist = dist;
            nearest = node;
          }
        });

        console.log(
          `🎯 Nearest node to [${coord}]: node ${nearest?.id} at distance ${minDist}`,
        );
        return nearest;
      };

      const startNode = findNearestNode(start);
      const endNode = findNearestNode(end);

      if (!startNode || !endNode) {
        console.warn("❌ Could not find nearest routing nodes");
        return null;
      }

      // Ensure start and end nodes are connected even if isolated
      ensureConnected(startNode.id);
      ensureConnected(endNode.id);

      console.log("🚀 Start node:", startNode.id, "coords:", [
        startNode.longitude,
        startNode.latitude,
      ]);
      console.log("🎯 End node:", endNode.id, "coords:", [
        endNode.longitude,
        endNode.latitude,
      ]);

      // Debug: Check start node's neighbors
      const startNodeInfo = graph.get(startNode.id);
      console.log(
        `🔍 Start node ${startNode.id} has ${startNodeInfo?.neighbors.length || 0} neighbors:`,
        startNodeInfo?.neighbors,
      );

      // Debug: Check end node's neighbors
      const endNodeInfo = graph.get(endNode.id);
      console.log(
        `🔍 End node ${endNode.id} has ${endNodeInfo?.neighbors.length || 0} neighbors:`,
        endNodeInfo?.neighbors,
      );

      // A* pathfinding — heuristic uses Haversine meters for consistency with graph edges
      const haversineH = (
        lon1: number,
        lat1: number,
        lon2: number,
        lat2: number,
      ): number => {
        const R = 6371000;
        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLon = ((lon2 - lon1) * Math.PI) / 180;
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      };

      const heuristic = (nodeId: number) => {
        const node = graph.get(nodeId)?.node;
        if (!node) return Infinity;
        return haversineH(
          node.longitude,
          node.latitude,
          endNode.longitude,
          endNode.latitude,
        );
      };

      const openSet = new Set([startNode.id]);
      const closedSet = new Set<number>(); // Track visited nodes
      const cameFrom = new Map<number, number>();
      const gScore = new Map<number, number>();
      const fScore = new Map<number, number>();

      gScore.set(startNode.id, 0);
      fScore.set(startNode.id, heuristic(startNode.id));

      let iterations = 0;
      const maxIterations = 10000; // Prevent infinite loops

      while (openSet.size > 0 && iterations < maxIterations) {
        iterations++;

        let current = -1;
        let lowestF = Infinity;
        openSet.forEach((nodeId) => {
          const f = fScore.get(nodeId) || Infinity;
          if (f < lowestF) {
            lowestF = f;
            current = nodeId;
          }
        });

        if (current === -1) {
          console.warn(
            `❌ No valid current node found in openSet at iteration ${iterations}`,
          );
          break;
        }

        if (current === endNode.id) {
          // Reconstruct path
          const path: number[] = [current];
          while (cameFrom.has(current)) {
            current = cameFrom.get(current)!;
            path.unshift(current);
          }

          // Convert to coordinates
          const coordinates: [number, number][] = [start];
          path.forEach((nodeId) => {
            const node = graph.get(nodeId)?.node;
            if (node) {
              coordinates.push([node.longitude, node.latitude]);
            }
          });
          coordinates.push(end);

          console.log(
            `✅ Found path with ${path.length} waypoints after ${iterations} iterations`,
          );

          return {
            geometry: {
              type: "LineString",
              coordinates,
            },
            distance: 0,
            duration: 0,
            legs: [],
          };
        }

        openSet.delete(current);
        closedSet.add(current); // Mark as visited

        if (iterations === 1) {
          console.log(
            `🗑️ Removed node ${current} from openSet and added to closedSet`,
          );
          console.log(
            `📊 OpenSet size before processing neighbors: ${openSet.size}`,
          );
          console.log(`📊 ClosedSet size: ${closedSet.size}`);
        }

        const currentNode = graph.get(current);
        if (!currentNode) {
          console.warn(`❌ Current node ${current} not found in graph`);
          continue;
        }

        console.log(
          `🔄 Iteration ${iterations}: Processing node ${current} with ${currentNode.neighbors.length} neighbors`,
        );

        currentNode.neighbors.forEach((neighborId) => {
          // Skip if already visited
          if (closedSet.has(neighborId)) {
            if (iterations === 1 && neighborId === endNode.id) {
              console.log(`⚠️ End node ${neighborId} is already in closedSet!`);
            }
            return;
          }

          const neighbor = graph.get(neighborId);
          if (!neighbor) {
            console.warn(`⚠️ Neighbor ${neighborId} not found in graph`);
            return;
          }

          const lon1 = currentNode.node.longitude;
          const lat1 = currentNode.node.latitude;
          const lon2 = neighbor.node.longitude;
          const lat2 = neighbor.node.latitude;

          // Use Haversine meters — consistent with graph building and heuristic
          const R = 6371000;
          const dLat = ((lat2 - lat1) * Math.PI) / 180;
          const dLon = ((lon2 - lon1) * Math.PI) / 180;
          const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos((lat1 * Math.PI) / 180) *
              Math.cos((lat2 * Math.PI) / 180) *
              Math.sin(dLon / 2) ** 2;
          const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

          if (isNaN(dist) || !isFinite(dist)) {
            console.warn(
              `⚠️ Invalid distance calculated for neighbor ${neighborId}: ${dist}`,
            );
            return;
          }

          const currentGScoreValue = gScore.get(current);
          if (
            currentGScoreValue === undefined ||
            isNaN(currentGScoreValue) ||
            !isFinite(currentGScoreValue)
          ) {
            console.warn(
              `⚠️ Invalid gScore for current node ${current}: ${currentGScoreValue}`,
            );
            return;
          }

          const tentativeGScore = currentGScoreValue + dist;
          const currentGScore = gScore.get(neighborId) || Infinity;

          if (iterations === 1 && neighborId === endNode.id) {
            console.log(`🎯 Found end node ${neighborId} as neighbor!`);
            console.log(`   Current node gScore: ${currentGScoreValue}`);
            console.log(`   Distance: ${dist}`);
            console.log(`   Current gScore: ${currentGScore}`);
            console.log(`   Tentative gScore: ${tentativeGScore}`);
            console.log(`   Will update: ${tentativeGScore < currentGScore}`);
          }

          if (tentativeGScore < currentGScore) {
            cameFrom.set(neighborId, current);
            gScore.set(neighborId, tentativeGScore);
            fScore.set(neighborId, tentativeGScore + heuristic(neighborId));

            if (!openSet.has(neighborId)) {
              openSet.add(neighborId);
              if (iterations === 1) {
                console.log(`   ✅ Added neighbor ${neighborId} to openSet`);
              }
            }
          }
        });

        if (iterations === 1) {
          console.log(
            `📊 OpenSet size after processing neighbors: ${openSet.size}`,
          );
          console.log(`📋 OpenSet contents:`, Array.from(openSet));
        }
      }

      console.warn(
        `❌ No path found after ${iterations} iterations. OpenSet size: ${openSet.size}`,
      );
      return null;
    } catch (error) {
      console.error("Failed to fetch indoor route:", error);
      return null;
    }
  };

  // Add function to fetch Mapbox directions
  const fetchMapboxDirections = async (
    start: [number, number],
    end: [number, number],
  ) => {
    try {
      const url = `https://api.mapbox.com/directions/v5/mapbox/walking/${start[0]},${start[1]};${end[0]},${end[1]}?steps=true&geometries=geojson&access_token=${MAPBOX_API_KEY}`;
      console.log("Mapbox URL:", url);

      const response = await fetch(url);
      const data = await response.json();

      console.log("Mapbox response status:", response.status);
      console.log("Mapbox response data:", data);

      if (data.code !== "Ok") {
        console.error("Mapbox error code:", data.code, data.message);
        return null;
      }

      if (data.routes && data.routes.length > 0) {
        console.log(
          "Route found, distance:",
          data.routes[0].distance,
          "duration:",
          data.routes[0].duration,
        );
        return data.routes[0];
      }
      console.warn("No routes in response");
      return null;
    } catch (error) {
      console.error("Failed to fetch Mapbox directions:", error);
      return null;
    }
  };

  // Draw Mapbox directions route
  useEffect(() => {
    // Use selectedDestination if available, otherwise use selectedDepartment
    const destination = selectedDestination || selectedDepartment;
    if (!mapRef.current || !destination) {
      console.log(
        "Skipping directions - map:",
        !!mapRef.current,
        "destination:",
        !!destination,
        "mapLoaded:",
        mapLoadedRef.current,
      );
      return;
    }

    const map = mapRef.current;
    const pixelsPerMeter = 10;
    const earthRadius = 6371000;
    const baseLat = 24.812;
    const baseLon = 46.624;

    // Helper function to convert cartesian to lat/lon
    const cartesianToLatLon = (x: number, y: number) => {
      const yMeters = y / pixelsPerMeter;
      const xMeters = x / pixelsPerMeter;
      const lat = baseLat + (yMeters / earthRadius) * (180 / Math.PI);
      const lon =
        baseLon +
        (xMeters / (earthRadius * Math.cos((baseLat * Math.PI) / 180))) *
          (180 / Math.PI);
      return [lon, lat];
    };

    // Default kiosk coordinates (fallback to SAB ATM - Gate 1 location)
    let kioskCoords = cartesianToLatLon(0, -150) as [number, number];

    // Prefer explicit kiosk data passed from parent (HomeScreen)
    if (kioskData) {
      try {
        if (
          kioskData.coordinates &&
          kioskData.coordinates.latitude &&
          kioskData.coordinates.longitude
        ) {
          kioskCoords = [
            kioskData.coordinates.longitude,
            kioskData.coordinates.latitude,
          ];
        } else if (kioskData.coordinates) {
          kioskCoords = cartesianToLatLon(
            kioskData.coordinates.x || 0,
            kioskData.coordinates.y || 0,
          ) as [number, number];
        }
      } catch (err) {
        console.warn("Invalid kioskData provided", err);
      }
    }

    // Get destination coordinates
    let destLat = destination.coordinates.latitude;
    let destLon = destination.coordinates.longitude;

    if (!destLat || !destLon) {
      const destCartesian = cartesianToLatLon(
        destination.coordinates.x,
        destination.coordinates.y,
      );
      destLon = destCartesian[0];
      destLat = destCartesian[1];
    }

    const displayRoute = (routeData: any) => {
      // Update route line with Mapbox directions geometry
      console.log(
        "Map sources available:",
        Object.keys(map.getStyle().sources || {}),
      );
      let source = map.getSource("route-line-source") as mapboxgl.GeoJSONSource;

      if (!source) {
        console.warn("route-line-source not found, creating...");
        // Create source if it doesn't exist
        map.addSource("route-line-source", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });
        source = map.getSource("route-line-source") as mapboxgl.GeoJSONSource;
      }

      if (!source) {
        console.error("Failed to get or create route-line-source");
        return;
      }

      console.log("Setting route data...");
      try {
        source.setData({
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: routeData.geometry,
              properties: {},
            },
          ],
        });
      } catch (err) {
        console.error("Error setting route data:", err);
        return;
      }

      console.log("Route displayed on map successfully");

      // Store route coordinates for simulation
      const coords = routeData.geometry.coordinates;
      if (coords && coords.length > 0) {
        setRouteCoordinates(coords as [number, number][]);
      }
    };

    const fetchAndDisplayDirections = async () => {
      try {
        console.log("=== STARTING ROUTE FETCH ===");

        // Skip re-fetch while navigation is in progress (prevents cross-floor loop)
        if (isNavigatingRef.current) {
          return;
        }
        // Elevator connection points (Ground Floor → B1)
        const elevatorConnections: [number, number][] = [
          [46.6230146, 24.8115571],
          [46.623091, 24.8114073],
          [46.6231542, 24.8114356],
          [46.6236899, 24.8116216],
          [46.6237822, 24.8116553],
          [46.6241997, 24.812056],
          [46.6242721, 24.8119158],
          [46.6243375, 24.8119465],
          [46.6242677, 24.8120865],
        ];

        const haversineM = (a: [number, number], b: [number, number]) => {
          const R = 6371000;
          const dLat = ((b[1] - a[1]) * Math.PI) / 180;
          const dLon = ((b[0] - a[0]) * Math.PI) / 180;
          const s =
            Math.sin(dLat / 2) ** 2 +
            Math.cos((a[1] * Math.PI) / 180) *
              Math.cos((b[1] * Math.PI) / 180) *
              Math.sin(dLon / 2) ** 2;
          return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
        };

        const destFloor = (destination as any)?.floor || "G";
        const kioskFloorNav = (kioskData as any)?.floor || "G";
        // Cross-floor only when kiosk and destination are on different floors
        const isCrossFloor = destFloor !== kioskFloorNav;

        // Save start/dest for post-navigation route restore
        savedKioskCoordsRef.current = kioskCoords;
        savedDestCoordsRef.current = [destLon, destLat];
        savedKioskFloorRef.current = kioskFloorNav;

        if (isCrossFloor) {
          // Step 1: Find nearest elevator to kiosk
          const nearestElevator = elevatorConnections.reduce((best, e) =>
            haversineM(kioskCoords, e) < haversineM(kioskCoords, best)
              ? e
              : best,
          );

          console.log(
            `Cross-floor route: ${kioskFloorNav} → elevator → ${destFloor}`,
          );
          console.log("Nearest elevator:", nearestElevator);

          // Step 2: Route on kiosk's floor from kiosk to elevator
          // Pass kioskFloorNav explicitly to avoid stale activeFloor closure
          const groundRoute = await fetchIndoorRoute(
            kioskCoords,
            nearestElevator,
            kioskFloorNav,
          );

          if (groundRoute) {
            displayRoute(groundRoute);
            if (onRouteFound) {
              onRouteFound({
                distance: groundRoute.distance,
                duration: groundRoute.duration,
                steps: [],
                geometry: groundRoute.geometry,
              });
            }
            // Mark this as a cross-floor route so startSimulation knows to transition
            (window as any).__crossFloorElevator = nearestElevator;
            (window as any).__crossFloorDest = [destLon, destLat];
            (window as any).__crossFloorTarget = destFloor;
          } else {
            console.warn("❌ No ground floor route to elevator found");
          }
        } else {
          // Same-floor routing
          (window as any).__crossFloorElevator = null;
          const indoorRoute = await fetchIndoorRoute(kioskCoords, [
            destLon,
            destLat,
          ]);
          if (!indoorRoute) {
            console.warn("❌ No indoor route found");
            return;
          }
          displayRoute(indoorRoute);
          if (onRouteFound) {
            onRouteFound({
              distance: indoorRoute.distance,
              duration: indoorRoute.duration,
              steps: [],
              geometry: indoorRoute.geometry,
            });
          }
        }
      } catch (error) {
        console.error("❌ Error fetching indoor route:", error);
      }
    };

    // If map is not loaded yet, wait for it
    if (!mapLoadedRef.current) {
      console.log("Map not loaded yet, waiting...");
      const checkMapLoaded = setInterval(() => {
        if (mapLoadedRef.current) {
          console.log("Map loaded, triggering route fetch");
          clearInterval(checkMapLoaded);
          fetchAndDisplayDirections();
        }
      }, 100);
      return () => clearInterval(checkMapLoaded);
    }

    fetchAndDisplayDirections();
  }, [selectedDestination, selectedDepartment, kioskId, onRouteFound]);

  // Switch floor — swap GeoJSON source and clear markers
  const switchFloor = (floor: "G" | "B1") => {
    if (!mapRef.current || !mapLoadedRef.current) return;
    setActiveFloor(floor);
    const map = mapRef.current;
    const geojsonFile =
      floor === "B1"
        ? "/B1_floor_map_nav.geojson"
        : "/ground_floor_combined.geojson";
    const src = map.getSource("ground-floor") as mapboxgl.GeoJSONSource;
    if (src) src.setData(geojsonFile);

    // Clear route line only on manual UI floor switches, not automatic ones
    const isAuto = isAutoFloorSwitchRef.current;
    if (!isAuto) {
      const routeSrc = map.getSource(
        "route-line-source",
      ) as mapboxgl.GeoJSONSource;
      if (routeSrc)
        routeSrc.setData({ type: "FeatureCollection", features: [] });
    }

    // Only clear route state if this is a manual floor switch (not cross-floor navigation)
    if (!(window as any).__crossFloorTransitioning && !isAuto) {
      setRouteCoordinates([]);
      stopSimulation();
    }

    // Show/hide destination pin based on whether destination is on this floor
    const destination = (window as any).__currentDestination;
    const destFloor = destination ? (destination as any)?.floor || "G" : null;
    if (destPinMarkerRef.current) {
      const pinEl = destPinMarkerRef.current.getElement() as HTMLElement;
      if (pinEl) pinEl.style.display = destFloor === floor ? "" : "none";
    }

    // Highlight filter is re-applied by the highlight useEffect when activeFloor changes

    // Clear markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current.clear();

    // Show/hide kiosk "You are here" markers — compare kiosk's floor directly
    const kioskFloor = (kioskData as any)?.floor || "G";
    const kioskOnThisFloor = kioskFloor === floor;
    kioskMarkersRef.current.forEach((m) => {
      const el = m.getElement();
      if (el) el.style.display = kioskOnThisFloor ? "" : "none";
    });

    // Notify parent to clear selected destination
    onFloorChange?.(floor);
  };

  // Navigation simulation functions
  const startSimulationWithCoords = (coords: [number, number][]) => {
    if (!mapRef.current || coords.length === 0) return;
    setRouteCoordinates(coords);
    runSimulation(coords);
  };

  const startSimulation = () => {
    if (!mapRef.current || routeCoordinates.length === 0) return;
    runSimulation(routeCoordinates);
  };

  const runSimulation = (coords: [number, number][]) => {
    if (!mapRef.current) return;

    isNavigatingRef.current = true;
    setIsSimulating(true);
    const map = mapRef.current;

    map.easeTo({ zoom: 19.5, pitch: 70, duration: 1500 });

    // Human figure SVG marker
    const userDotEl = document.createElement("div");
    userDotEl.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 24 24">
        <circle cx="12" cy="4.5" r="3" fill="#00b878" stroke="white" stroke-width="1"/>
        <path d="M12 9c-2.8 0-4.5 1.7-4.5 3.5V17h3v5h3v-5h3v-4.5C16.5 10.7 14.8 9 12 9z"
              fill="#00b878" stroke="white" stroke-width="0.5"/>
      </svg>`;
    userDotEl.style.cssText = `
      width:52px; height:52px;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
      pointer-events: none;
    `;

    const userMarker = new mapboxgl.Marker({
      element: userDotEl,
      anchor: "bottom",
    })
      .setLngLat(coords[0])
      .addTo(map);

    (simulationRef as any).userMarker = userMarker;

    // --- Smooth continuous animation via requestAnimationFrame ---
    // Speed: ~1.4 m/s walking speed. Use Haversine to get real distance.
    const haversineTotal = (pts: [number, number][]) => {
      let d = 0;
      for (let i = 0; i < pts.length - 1; i++) {
        const R = 6371000;
        const dLat = ((pts[i + 1][1] - pts[i][1]) * Math.PI) / 180;
        const dLon = ((pts[i + 1][0] - pts[i][0]) * Math.PI) / 180;
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos((pts[i][1] * Math.PI) / 180) *
            Math.cos((pts[i + 1][1] * Math.PI) / 180) *
            Math.sin(dLon / 2) ** 2;
        d += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      }
      return d;
    };
    const routeDistanceM = haversineTotal(coords as [number, number][]);
    // Animation speed: 5 m/s, clamped between 2s and 5s
    const TOTAL_DURATION_MS = Math.min(
      5000,
      Math.max(1000, (routeDistanceM / 20) * 1000),
    );

    // Pre-compute cumulative distances for uniform speed
    const segLengths: number[] = [];
    let totalLength = 0;
    for (let i = 0; i < coords.length - 1; i++) {
      const dx = coords[i + 1][0] - coords[i][0];
      const dy = coords[i + 1][1] - coords[i][1];
      const len = Math.sqrt(dx * dx + dy * dy);
      segLengths.push(len);
      totalLength += len;
    }
    const cumLengths: number[] = [0];
    for (let i = 0; i < segLengths.length; i++) {
      cumLengths.push(cumLengths[i] + segLengths[i]);
    }

    const startTime = performance.now();

    const tick = (now: number) => {
      const t = Math.min((now - startTime) / TOTAL_DURATION_MS, 1);
      const targetDist = t * totalLength;

      // Binary-search for the segment
      let segIdx = segLengths.length - 1;
      for (let i = 0; i < cumLengths.length - 1; i++) {
        if (targetDist <= cumLengths[i + 1]) {
          segIdx = i;
          break;
        }
      }

      const segT =
        segLengths[segIdx] === 0
          ? 0
          : (targetDist - cumLengths[segIdx]) / segLengths[segIdx];

      const lng =
        coords[segIdx][0] +
        (coords[Math.min(segIdx + 1, coords.length - 1)][0] -
          coords[segIdx][0]) *
          segT;
      const lat =
        coords[segIdx][1] +
        (coords[Math.min(segIdx + 1, coords.length - 1)][1] -
          coords[segIdx][1]) *
          segT;

      userMarker.setLngLat([lng, lat]);

      const bearing = calculateBearing(
        coords[segIdx],
        coords[Math.min(segIdx + 1, coords.length - 1)],
      );

      map.easeTo({
        center: [lng, lat],
        bearing,
        duration: 100,
        easing: (x) => x,
      });

      if (t < 1) {
        (simulationRef as any).rafId = requestAnimationFrame(tick);
      } else {
        userMarker.remove();
        setIsSimulating(false);

        // Check if this is a cross-floor route (G → B1 via elevator)
        const elevatorCoord = (window as any).__crossFloorElevator as
          | [number, number]
          | null;
        const b1Dest = (window as any).__crossFloorDest as
          | [number, number]
          | null;

        if (elevatorCoord && b1Dest) {
          // Clear the cross-floor markers so we don't re-trigger
          (window as any).__crossFloorElevator = null;
          (window as any).__crossFloorDest = null;

          // Show elevator transition overlay
          const targetFloor = ((window as any).__crossFloorTarget || "B1") as
            | "G"
            | "B1";
          const sourceFloor = targetFloor === "B1" ? "G" : "B1";
          (window as any).__crossFloorTarget = null;
          setTransitionFromFloor(sourceFloor);
          setTransitionTarget(targetFloor);
          setIsFloorTransitioning(true);
          setTimeout(async () => {
            setIsFloorTransitioning(false);
            // Set flag so switchFloor doesn't clear route coordinates
            (window as any).__crossFloorTransitioning = true;
            switchFloor(targetFloor);

            // Wait for target floor GeoJSON to load, then route from elevator to destination
            setTimeout(async () => {
              const b1Route = await fetchIndoorRoute(
                elevatorCoord,
                b1Dest,
                targetFloor,
              );
              if (b1Route && mapRef.current) {
                const src = mapRef.current.getSource(
                  "route-line-source",
                ) as mapboxgl.GeoJSONSource;
                if (src) {
                  src.setData({
                    type: "FeatureCollection",
                    features: [
                      {
                        type: "Feature",
                        geometry: b1Route.geometry as any,
                        properties: {},
                      },
                    ],
                  } as any);
                }
                setRouteCoordinates(
                  b1Route.geometry.coordinates as [number, number][],
                );
                // Pan map to show the route on the new floor
                mapRef.current.easeTo({
                  center: elevatorCoord,
                  zoom: 19,
                  pitch: 55,
                  bearing: 66,
                  duration: 800,
                });
                // Clear the flag now that route is set
                (window as any).__crossFloorTransitioning = false;
                // Auto-start navigation on the new floor
                setTimeout(() => {
                  runSimulation(
                    b1Route.geometry.coordinates as [number, number][],
                  );
                }, 900);
              } else {
                console.warn(
                  `❌ No route found on ${targetFloor} from elevator to destination`,
                );
                (window as any).__crossFloorTransitioning = false;
              }
            }, 800);
          }, 3500);
        } else {
          // Navigation complete — pan back to kiosk, restore original route
          (window as any).__crossFloorElevator = null;
          (window as any).__crossFloorDest = null;
          (window as any).__crossFloorTarget = null;
          (window as any).__crossFloorTransitioning = false;

          const kioskFloor = (kioskData as any)?.floor || "G";
          const kioskLat = kioskData?.coordinates?.latitude;
          const kioskLon = kioskData?.coordinates?.longitude;

          // Pan back to kiosk first
          if (kioskLat && kioskLon) {
            const bearing = calcBearingToBuilding(kioskLon, kioskLat);
            const [cLon, cLat] = offsetCenter(kioskLon, kioskLat, bearing);
            map.easeTo({
              center: [cLon, cLat],
              zoom: 19,
              pitch: 55,
              bearing: 66,
              duration: 1500,
            });
          } else {
            map.easeTo({ zoom: 19, pitch: 55, bearing: 66, duration: 1500 });
          }

          // After pan: restore kiosk floor, re-draw original route, release lock
          setTimeout(async () => {
            const currentMap = mapRef.current;
            if (!currentMap || !mapLoadedRef.current) {
              isNavigatingRef.current = false;
              return;
            }

            // Swap GeoJSON back to kiosk floor
            const geojsonFile =
              kioskFloor === "B1"
                ? "/B1_floor_map_nav.geojson"
                : "/ground_floor_combined.geojson";
            const src = currentMap.getSource(
              "ground-floor",
            ) as mapboxgl.GeoJSONSource;
            if (src) src.setData(geojsonFile);

            // Update React floor state (isNavigatingRef still true — blocks route useEffect)
            setActiveFloor(kioskFloor as "G" | "B1");

            // Show kiosk marker on restored floor
            kioskMarkersRef.current.forEach((m) => {
              const el = m.getElement();
              if (el) el.style.display = "";
            });

            // Re-fetch and display the original full route from kiosk to destination
            const startCoords = savedKioskCoordsRef.current;
            const endCoords = savedDestCoordsRef.current;
            const startFloor = savedKioskFloorRef.current;

            if (startCoords && endCoords) {
              // Wait a tick for GeoJSON to load before routing
              await new Promise((r) => setTimeout(r, 400));

              const elevatorConnections: [number, number][] = [
                [46.6230146, 24.8115571],
                [46.623091, 24.8114073],
                [46.6231542, 24.8114356],
                [46.6236899, 24.8116216],
                [46.6237822, 24.8116553],
                [46.6241997, 24.812056],
                [46.6242721, 24.8119158],
                [46.6243375, 24.8119465],
                [46.6242677, 24.8120865],
              ];
              const haversineM = (a: [number, number], b: [number, number]) => {
                const R = 6371000;
                const dLat = ((b[1] - a[1]) * Math.PI) / 180;
                const dLon = ((b[0] - a[0]) * Math.PI) / 180;
                const s =
                  Math.sin(dLat / 2) ** 2 +
                  Math.cos((a[1] * Math.PI) / 180) *
                    Math.cos((b[1] * Math.PI) / 180) *
                    Math.sin(dLon / 2) ** 2;
                return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
              };

              const destFloorSaved =
                ((window as any).__currentDestination as any)?.floor || "G";
              const isCrossFloorRestore = destFloorSaved !== startFloor;

              let restoredRoute = null;
              if (isCrossFloorRestore) {
                // Cross-floor: route from kiosk to nearest elevator on kiosk floor
                const nearestElevator = elevatorConnections.reduce((best, e) =>
                  haversineM(startCoords, e) < haversineM(startCoords, best)
                    ? e
                    : best,
                );
                restoredRoute = await fetchIndoorRoute(
                  startCoords,
                  nearestElevator,
                  startFloor,
                );
              } else {
                // Same floor: full route kiosk → destination
                restoredRoute = await fetchIndoorRoute(
                  startCoords,
                  endCoords,
                  startFloor,
                );
              }

              if (restoredRoute && currentMap) {
                const routeSrc = currentMap.getSource(
                  "route-line-source",
                ) as mapboxgl.GeoJSONSource;
                if (routeSrc) {
                  routeSrc.setData({
                    type: "FeatureCollection",
                    features: [
                      {
                        type: "Feature",
                        geometry: restoredRoute.geometry as any,
                        properties: {},
                      },
                    ],
                  } as any);
                }
                setRouteCoordinates(
                  restoredRoute.geometry.coordinates as [number, number][],
                );
              }
            }

            // Release navigation lock
            isNavigatingRef.current = false;
          }, 1600);
        }
      }
    };

    (simulationRef as any).rafId = requestAnimationFrame(tick);
  };

  const stopSimulation = () => {
    if ((simulationRef as any).rafId) {
      cancelAnimationFrame((simulationRef as any).rafId);
      (simulationRef as any).rafId = null;
    }
    if (simulationRef.current) {
      clearTimeout(simulationRef.current);
      simulationRef.current = null;
    }
    if ((simulationRef as any).userMarker) {
      (simulationRef as any).userMarker.remove();
      (simulationRef as any).userMarker = null;
    }
    setIsSimulating(false);

    // Restore kiosk-centered view dynamically
    if (
      mapRef.current &&
      kioskData?.coordinates?.latitude &&
      kioskData?.coordinates?.longitude
    ) {
      const lat = kioskData.coordinates.latitude;
      const lon = kioskData.coordinates.longitude;
      const bearing = calcBearingToBuilding(lon, lat);
      const [cLon, cLat] = offsetCenter(lon, lat, bearing);
      mapRef.current.easeTo({
        center: [cLon, cLat],
        zoom: 19,
        pitch: 55,
        bearing: 66,
        duration: 1000,
      });
    }
  };

  const calculateBearing = (
    from: [number, number],
    to: [number, number],
  ): number => {
    const [lon1, lat1] = from;
    const [lon2, lat2] = to;

    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const lat1Rad = (lat1 * Math.PI) / 180;
    const lat2Rad = (lat2 * Math.PI) / 180;

    const y = Math.sin(dLon) * Math.cos(lat2Rad);
    const x =
      Math.cos(lat1Rad) * Math.sin(lat2Rad) -
      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);

    const bearing = (Math.atan2(y, x) * 180) / Math.PI;
    return (bearing + 360) % 360;
  };

  // Cleanup simulation on unmount
  useEffect(() => {
    return () => {
      if (simulationRef.current) {
        clearTimeout(simulationRef.current);
      }
    };
  }, []);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div
        style={{
          flex: 1,
          borderRadius: "8px",
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 3px 6px rgba(0,0,0,0.16)",
        }}
      >
        <div
          ref={mapContainer}
          style={{ width: "100%", height: "100%", background: "#0F172A" }}
        />

        {/* Floor Selector — recenter only, floor buttons hidden */}
        <div
          style={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            background: "var(--surface-2)",
            borderRadius: 10,
            padding: "6px 4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          }}
        >
          {/* Recenter button only — floor G/B1 buttons hidden from user */}
          <button
            onClick={() => {
              if (!mapRef.current) return;
              const lat = kioskData?.coordinates?.latitude;
              const lon = kioskData?.coordinates?.longitude;
              if (lat && lon) {
                const bearing = calcBearingToBuilding(lon, lat);
                const [cLon, cLat] = offsetCenter(lon, lat, bearing);
                mapRef.current.easeTo({
                  center: [cLon, cLat],
                  zoom: 19,
                  pitch: 55,
                  bearing: 66,
                  duration: 800,
                });
              } else {
                mapRef.current.easeTo({
                  center: [46.62315, 24.8115],
                  zoom: 19,
                  pitch: 55,
                  bearing: 66,
                  duration: 800,
                });
              }
            }}
            title="Recenter map"
            style={{
              width: 44,
              height: 36,
              border: "none",
              borderRadius: 7,
              background: "var(--surface-3)",
              color: "var(--color-text-secondary)",
              fontSize: 18,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
            }}
          >
            ⊕
          </button>
        </div>

        {/* Hide 3D button — hidden, 3D is always on */}

        {/* Floor transition overlay */}
        <FloorTransitionOverlay
          visible={isFloorTransitioning}
          fromFloor={transitionFromFloor}
          toFloor={transitionTarget}
        />

        {/* Simulation Control Button — placed next to recenter button, top-left */}
        {routeCoordinates.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: 16,
              left: 72,
              zIndex: 1000,
            }}
          >
            {!isSimulating ? (
              <button
                onClick={startSimulation}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#667eea",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  whiteSpace: "nowrap",
                }}
              >
                <span>▶</span> Start Navigation
              </button>
            ) : (
              <button
                onClick={stopSimulation}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  whiteSpace: "nowrap",
                }}
              >
                <span>■</span> Stop Navigation
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveMap;
